/**
 * PendingApprovalsCard
 * --------------------
 * Admin-dashboard homescreen widget surfacing pending hospital-registration
 * requests (self-signup + auto-drafted from new-customer orders) with
 * one-click Approve / Reject controls. Polls /api/admin/pending-approvals
 * every 60s. Visible to anyone with admin permission (full, orders_only,
 * view_only) — backend gating is done server-side.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Building2,
  CheckCircle2,
  XCircle,
  Loader2,
  Mail,
  ShoppingCart,
  Clock,
  ChevronRight,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const FALLBACK = { pending_count: 0, new_customer_order_count: 0, requests: [] };

export default function PendingApprovalsCard({ token }) {
  const [data, setData] = useState(FALLBACK);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  // After an approve that couldn't auto-deliver the welcome email, stash the
  // payload so we can show a one-click "Send via Outlook" button to the admin.
  // Keyed by the email address that failed so multiple parallel approvals
  // surface independently.
  const [pendingManualEmails, setPendingManualEmails] = useState({});
  // ?approve=<request_id> in the URL (from the email CTA) scrolls + flashes
  // the matching row so admins land directly on the request.
  const [searchParams, setSearchParams] = useSearchParams();
  const focusId = searchParams.get("approve");
  const rowRefs = useRef({});

  const headers = { Authorization: `Bearer ${token}` };

  // Build a mailto: URL the admin can click to open Outlook (or whatever
  // default mail client) pre-filled with the welcome letter contents.
  // mailto links cap at ~2000 chars in some clients; the plain-text body
  // from the backend stays well under that.
  const openInOutlook = (payload) => {
    const params = new URLSearchParams({
      subject: payload.subject || "Welcome to Sterile Mate",
      body: payload.plain_body || "",
    }).toString().replace(/\+/g, "%20");
    window.location.href = `mailto:${encodeURIComponent(payload.recipient)}?${params}`;
  };

  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/admin/pending-approvals`, { headers });
      setData(r.data || FALLBACK);
    } catch (_e) {
      // Silent — widget is best-effort; admin permission may not allow this endpoint
      setData(FALLBACK);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 60000);
    return () => clearInterval(t);
  }, [refresh]);

  // Scroll the deep-linked row into view + flash a ring once the request
  // list has loaded. Clear the URL param after to avoid re-triggering on
  // subsequent renders.
  useEffect(() => {
    if (!focusId || loading) return;
    const el = rowRefs.current[focusId];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-2", "ring-amber-400", "bg-amber-50/70");
      const t = setTimeout(() => {
        el.classList.remove("ring-2", "ring-amber-400", "bg-amber-50/70");
        const np = new URLSearchParams(searchParams);
        np.delete("approve");
        setSearchParams(np, { replace: true });
      }, 3500);
      return () => clearTimeout(t);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusId, loading, data.requests.length]);

  const approve = async (id, hospital, recipientEmail, { silent = false } = {}) => {
    setBusyId(id);
    try {
      const url = silent
        ? `${API}/api/hospitals/register/approve/${id}?skip_email=true`
        : `${API}/api/hospitals/register/approve/${id}`;
      const r = await axios.post(url, {}, { headers });
      const email = r.data?.email || {};
      if (silent || email.status === "skipped") {
        toast.success(`Approved silently · no welcome email sent`, {
          description: `${hospital} now has portal access. Share the access code directly.`,
        });
      } else if (email.status === "sent") {
        toast.success(`Approved · welcome email delivered to ${hospital}`);
      } else {
        toast.warning("Approved — but auto-send failed. Send the welcome email manually below.");
        setPendingManualEmails((prev) => ({
          ...prev,
          [recipientEmail || id]: {
            ...email,
            hospital_name: hospital,
            registration_id: id,
          },
        }));
      }
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Approval failed");
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (id) => {
    setBusyId(id);
    try {
      await axios.post(
        `${API}/api/hospitals/register/reject/${id}`,
        { reason: rejectReason || "Additional information required — our team will follow up." },
        { headers },
      );
      toast.success("Registration declined and registrant notified");
      setRejectingId(null);
      setRejectReason("");
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reject failed");
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center gap-2 text-slate-400 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading approvals…
        </div>
      </div>
    );
  }

  // Hide card entirely when nothing is pending — UNLESS we still have an
  // unsent manual welcome email surfaced. Otherwise approving the LAST pending
  // request would unmount the card and lose the mailto fallback before the
  // admin can use it.
  if (
    data.pending_count === 0
    && data.new_customer_order_count === 0
    && Object.keys(pendingManualEmails).length === 0
  ) return null;

  return (
    <div
      className="bg-white rounded-xl border border-amber-200 shadow-sm overflow-hidden"
      data-testid="pending-approvals-card"
    >
      <div className="bg-gradient-to-r from-amber-50 to-amber-100/60 px-5 py-3 border-b border-amber-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-amber-500 flex items-center justify-center">
            <Clock className="w-4 h-4 text-white" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-amber-900">Pending Hospital Approvals</h3>
            <p className="text-[11px] text-amber-700">
              {data.pending_count} {data.pending_count === 1 ? "request" : "requests"}
              {data.new_customer_order_count > 0 && (
                <span> · {data.new_customer_order_count} new-customer order{data.new_customer_order_count === 1 ? "" : "s"} on hold</span>
              )}
            </p>
          </div>
        </div>
        <button
          onClick={refresh}
          className="text-[11px] font-semibold text-amber-700 hover:text-amber-900 px-2 py-1 rounded hover:bg-amber-200/40 transition-colors"
          data-testid="approvals-refresh"
        >
          Refresh
        </button>
      </div>

      <div className="divide-y divide-slate-100 max-h-[420px] overflow-y-auto">
        {/* Manual-send fallback — surfaces when auto-delivery via Resend/Graph
            failed (e.g. Resend sandbox limiting to verified addresses). Admin
            can click "Send via Outlook" to open their default mail client with
            the welcome letter pre-filled, or copy the body to paste manually. */}
        {Object.entries(pendingManualEmails).map(([key, p]) => (
          <div key={`manual-${key}`} className="px-5 py-3 bg-amber-50 border-l-4 border-amber-400" data-testid={`manual-send-${p.registration_id || key}`}>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-amber-200 text-amber-800 flex items-center justify-center shrink-0 mt-0.5">
                <Mail className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-amber-900">
                  Send the welcome email manually
                </p>
                <p className="text-[12px] text-amber-800 mt-0.5 leading-snug">
                  Auto-delivery to <strong>{p.recipient}</strong> didn&apos;t reach the inbox
                  ({p.detail ? p.detail.slice(0, 90) + (p.detail.length > 90 ? "…" : "") : "unknown error"}).
                  Click below to open it in <strong>Outlook</strong> (or your default mail client) — pre-filled and ready to send from <strong>orders@sterilemate.com</strong>.
                </p>
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  <button
                    onClick={() => openInOutlook(p)}
                    className="px-3 py-1.5 text-xs font-bold rounded-md bg-amber-600 hover:bg-amber-700 text-white inline-flex items-center gap-1.5"
                    data-testid={`open-outlook-${p.registration_id || key}`}
                  >
                    <Mail className="w-3 h-3" /> Send via Outlook
                  </button>
                  <button
                    onClick={() => { navigator.clipboard.writeText(p.plain_body || ""); toast.success("Email body copied to clipboard"); }}
                    className="px-2.5 py-1.5 text-xs font-semibold rounded-md bg-white border border-amber-300 text-amber-900 hover:bg-amber-100"
                  >
                    Copy body
                  </button>
                  <button
                    onClick={() => setPendingManualEmails((prev) => { const n = { ...prev }; delete n[key]; return n; })}
                    className="text-xs text-amber-700 hover:text-amber-900 ml-auto"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}

        {data.requests.length === 0 ? (
          Object.keys(pendingManualEmails).length === 0 ? (
            <p className="text-sm text-slate-500 px-5 py-6 italic">No pending approvals.</p>
          ) : null
        ) : (
          data.requests.map((r) => (
            <div
              key={r.id}
              ref={(el) => { if (el) rowRefs.current[r.id] = el; }}
              className="px-5 py-4 transition-all duration-500"
              data-testid={`approval-row-${r.id}`}
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-cyan-100 flex items-center justify-center shrink-0">
                  <Building2 className="w-5 h-5 text-cyan-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-slate-900 text-sm truncate">{r.hospital_name}</p>
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full ${
                      r.payment_terms_preference === "Prepay"
                        ? "bg-emerald-100 text-emerald-700"
                        : "bg-amber-100 text-amber-700"
                    }`}>
                      {r.payment_terms_preference}
                    </span>
                    {r.source === "auto_from_order" && (
                      <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-violet-700 bg-violet-100 px-1.5 py-0.5 rounded-full">
                        <ShoppingCart className="w-2.5 h-2.5" /> auto · from order
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">
                    <strong className="text-slate-700">{r.contact_name}</strong>
                    {r.department && ` · ${r.department}`}
                    {(r.city || r.state) && ` · ${r.city || ""}${r.city && r.state ? ", " : ""}${r.state || ""}`}
                  </p>
                  <p className="text-[11px] text-slate-400 mt-0.5">
                    <Mail className="w-3 h-3 inline mr-1" />{r.email}
                  </p>

                  {rejectingId === r.id ? (
                    <div className="mt-3 space-y-2">
                      <textarea
                        rows={2}
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        placeholder="Reason (sent to registrant in the email)…"
                        className="w-full text-xs rounded-md border border-slate-300 px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-amber-500"
                        data-testid={`approval-reject-reason-${r.id}`}
                      />
                      <div className="flex items-center gap-2">
                        <button
                          disabled={busyId === r.id}
                          onClick={() => reject(r.id)}
                          className="px-3 py-1.5 text-xs font-semibold rounded-md bg-rose-600 hover:bg-rose-700 text-white disabled:opacity-50 inline-flex items-center gap-1"
                          data-testid={`approval-reject-confirm-${r.id}`}
                        >
                          {busyId === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <XCircle className="w-3 h-3" />}
                          Send Rejection
                        </button>
                        <button
                          onClick={() => { setRejectingId(null); setRejectReason(""); }}
                          className="text-xs text-slate-500 hover:text-slate-700"
                        >
                          cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 mt-3">
                      <button
                        disabled={busyId === r.id}
                        onClick={() => approve(r.id, r.hospital_name, r.email)}
                        className="px-3 py-1.5 text-xs font-bold rounded-md bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50 inline-flex items-center gap-1"
                        data-testid={`approval-approve-${r.id}`}
                      >
                        {busyId === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                        Approve &amp; Send Welcome
                      </button>
                      <button
                        disabled={busyId === r.id}
                        onClick={() => approve(r.id, r.hospital_name, r.email, { silent: true })}
                        className="px-3 py-1.5 text-xs font-semibold rounded-md bg-slate-700 hover:bg-slate-800 text-white disabled:opacity-50 inline-flex items-center gap-1"
                        title="Approve without sending the welcome letter — for existing customers already in your system."
                        data-testid={`approval-approve-silent-${r.id}`}
                      >
                        <CheckCircle2 className="w-3 h-3" /> Approve Silently
                      </button>
                      <button
                        onClick={() => { setRejectingId(r.id); setRejectReason(""); }}
                        className="px-3 py-1.5 text-xs font-semibold rounded-md bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1"
                        data-testid={`approval-reject-${r.id}`}
                      >
                        <XCircle className="w-3 h-3" /> Reject
                      </button>
                      {r.linked_order_id && (
                        <a
                          href={`/admin/orders?focus=${r.linked_order_id}`}
                          className="ml-auto text-[11px] text-slate-500 hover:text-slate-800 inline-flex items-center"
                        >
                          view order <ChevronRight className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
